import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage keys
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * 换一张新的 access token，并写回 localStorage。
 *
 * 抽出来是给 socket.ts 用的：socket 握手鉴权失败时也要续期，但**绝不能自己
 * 去打 /auth/refresh** —— 后端会轮换 refresh token（下面写回 REFRESH_TOKEN_KEY
 * 那行），两处并发各换一次，先返回的那张会被后返回的作废，用户直接被踢到登录页。
 * 所以 REST 拦截器和 socket 都走这个函数，共用 isRefreshing 闸门：已经在换的
 * 时候后来者进队列等结果，全程只会有一个 /auth/refresh 在飞。
 *
 * 失败时只抛错、不跳转登录页 —— 跳转由调用方决定（REST 拦截器跳，socket 不跳，
 * 免得两处抢着跳）。
 */
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // 已有一次续期在飞：排队等它的结果，不要再发一个请求
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({
        resolve: (value) => resolve(value as string),
        reject,
      });
    });
  }

  isRefreshing = true;
  try {
    // 用裸 axios 而不是 api 实例：走 api 会被 401 拦截器再次拦下，套娃
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });

    const newToken = response.data?.accessToken;
    if (!newToken) {
      throw new Error('Refresh response contained no accessToken');
    }

    localStorage.setItem(TOKEN_KEY, newToken);
    // 后端轮换了 refresh token 就一并存下，否则下次续期会拿着作废的那张
    if (response.data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    }

    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Extract data from success response format: { success: true, data: ... }
    if (response.data && response.data.success !== undefined) {
      // Check if it's a paginated response (has total, limit, offset)
      if (response.data.total !== undefined && response.data.limit !== undefined) {
        // Preserve pagination metadata by removing only the success field
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { success, ...rest } = response.data;
        return { ...response, data: rest };
      }
      // For non-paginated responses, extract the data
      return { ...response, data: response.data.data || response.data };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 error and not a login/refresh request
    if (error.response?.status === 401 && !originalRequest._retry) {
      const isLoginRequest = originalRequest.url?.includes('/auth/login');
      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

      // Don't try to refresh for login or refresh requests
      if (isLoginRequest || isRefreshRequest) {
        return Promise.reject(error);
      }

      // Check if we have a refresh token
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // 排队/加锁/写回 localStorage 都在 refreshAccessToken 里统一处理，
      // 这里只负责"拿到新 token 后重放这个请求"和"彻底失败就跳登录页"
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle validation errors
    if (error.response?.status === 400 && error.response?.data?.errors) {
      const validationErrors = error.response.data.errors
        .map((err: { field: string; message: string }) => `${err.field}: ${err.message}`)
        .join(', ');
      error.message = validationErrors;
    }

    return Promise.reject(error);
  }
);

// ==================== Types ====================

interface User {
  id: number;
  username: string;
  email: string;
  role: 'boss' | 'dispatcher' | 'driver' | 'accountant' | 'docs_clerk' | 'manager' | 'platform_admin';
  company_id?: number;
  company_name?: string;
  created_at: string;
}

export interface DispatchTask {
  id: number;
  task_id?: string;
  container_id?: number;
  driver_id?: number;
  truck_id?: number;
  task_type: 'load-out' | 'load-in' | 'empty-in' | 'empty-out' | 'delivery' | 'return';
  task_subtype?: 'drop' | 'live-load';
  status: 'on-vessel' | 'released' | 'on-hold' | 'unassigned' | 'assigned' | 'picking' | 'delivering' | 'on-stop' | 'completed' | 'loading' | 'empty' | 'loaded' | 'returning' | 'cancelled' | 'paused';
  bol_number?: string;
  appointment_time?: string;
  pickup_location?: string;
  delivery_location?: string;
  estimated_duration?: number;
  actual_start_time?: string;
  actual_end_time?: string;
  dispatcher_notes?: string;
  driver_notes?: string;
  dispatcher_instructions?: string;
  order_id?: number;
  created_at: string;
  updated_at?: string;
}

interface Company {
  id: number;
  name: string;
  type: 'shipper' | 'warehouse' | 'carrier' | 'customer';
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  tax_id?: string;
  payment_terms?: 'NET_15' | 'NET_30' | 'NET_45' | 'NET_60' | 'COD' | 'PREPAID';
  credit_limit?: number;
  status?: 'active' | 'inactive' | 'suspended';
  notes?: string;
  created_at: string;
  updated_at?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

interface Stats {
  [key: string]: string | number;
}

export interface OrderTask {
  id: number;
  taskId: string;
  type: string;
  subtype?: string;
  taskSubtype?: string;
  status: string;
  driverId?: number;
  driver?: string;
  truckId?: number;
  appointment?: string;
  pickupLocation?: string;
  deliveryLocation?: string;
  dispatcherNotes?: string;
  driverNotes?: string;
  // Route info
  originType?: string;
  originId?: number;
  destinationType?: string;
  destinationId?: number;
  routeNodes?: any;
  originalRouteNodes?: any;
  routeModificationCount?: number;
  // Appointments
  portAppointment?: string;
  portAppointmentStatus?: string;
  warehouseAppointment?: string;
  warehouseAppointmentStatus?: string;
  // Reroute
  rerouted?: boolean;
  rerouteReason?: string;
  rerouteAt?: string;
  // Location names
  portName?: string;
  portShortName?: string;
  warehouseName?: string;
  warehouseShortName?: string;
  yardName?: string;
  yardShortName?: string;
  returnLocationType?: string;
  returnLocationId?: number;
  returnLocationName?: string;
  returnLocationShortName?: string;
  returnLocationPending?: boolean;
  // Task metadata
  taskSequence?: number;
  importExport?: string;
  containerId?: number;
  parentTaskId?: number;
  pendingYardStorage?: boolean;
  pendingPccInspection?: boolean;
  taskStreetTurn?: boolean;
  taskStreetTurnFromOrderId?: number;
}

export interface Order {
  id?: number;  // Database primary key (numeric)
  order_id: string;  // Display order ID (e.g., "ORD-001")
  container_id?: number;
  container_number: string;
  shipping_line?: string;
  shipping_line_code?: string;
  container_size?: string;
  container_type?: string;
  container_status?: string;
  shipper: string;
  shipper_contact_name?: string;
  shipper_contact_phone?: string;
  shipper_contact_email?: string;
  warehouse_id?: number;
  warehouse_appointment?: string;
  port_appointment?: string;
  lfd?: string;
  internal_note?: string;
  special_instructions?: string;
  type?: 'Drop&Return' | 'Liveload';
  // Import/Export type - determines business workflow
  import_export?: 'import' | 'export';
  task_count?: number;
  created_at: string;
  status: string;
  // Destination warehouse fields (from warehouse_clients table)
  destination_warehouse_id?: number;
  destination_warehouse_name?: string;
  destination_warehouse_contact?: string;
  destination_warehouse_phone?: string;
  destination_warehouse_address?: string;
  tasks: OrderTask[];
  // Invoice fields
  invoice_id?: number;
  invoice_invoice_id?: string;
  invoice_number?: string;
  invoice_status?: string;
  invoice_rate_type?: string;
  invoice_pricing_type?: string;
  invoice_base_amount?: number;
  invoice_extra_amount?: number;
  invoice_tax_amount?: number;
  invoice_discount_amount?: number;
  invoice_total_amount?: number;
  invoice_payment_term?: string;
  invoice_issued_date?: string;
  invoice_due_date?: string;
  driver_rate_type?: string;
  total_amount?: number;
  bol_number?: string;
  notes?: string;
  completed_at?: string;
  task_ids?: string;
  client_name?: string;
  client_address?: string;
  // Three-party role fields (三问判定法)
  ordering_party_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  ordering_party_id?: number;
  ordering_party_name?: string;  // Resolved name for display
  bill_to_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  bill_to_id?: number;
  bill_to_name?: string;  // Resolved name for display
  deliver_to_type?: 'client' | 'shipper' | 'warehouse' | 'port';
  deliver_to_id?: number;
  deliver_to_name?: string;  // Resolved name for display
  // Legacy fields (for backward compatibility)
  shipper_id?: number;
  client_id?: number;
  chassis_id?: number;
  chassis_number?: string;
  // Street Turn fields
  street_turn?: boolean;
  street_turn_from_order_id?: number;
  street_turn_status?: 'pending' | 'approved' | 'rejected';
  street_turn_approved_at?: string;
  street_turn_notes?: string;
  street_turn_approval_number?: string;
  export_booking_number?: string;
  // Additional shipping fields
  entry_number?: string;
  eta?: string;
  house_number?: string;
  // 只写在客户邮件正文里的那几样（migration 196）：派送单 PDF 上一个都没有。
  // 交付窗口两端是 YYYY-MM-DD 字符串，不是 Date —— 别再过一遍时区换算。
  all_in_rate?: number | string | null;
  warehouse_appointment_number?: string | null;
  delivery_window_start?: string | null;
  delivery_window_end?: string | null;
  // 要哪种发电机（migration 201）。四态：'genset' 独立机器 / 'genset_chassis'
  // 焊在底盘上 / 'none' 明确不要 / null 没人说过（存量订单）。
  genset_type?: 'none' | 'genset' | 'genset_chassis' | null;
  // Shipment Group fields (On Bill / 一单多柜)
  shipment_group_id?: number | null;
  shipment_group_number?: string | null;
  container_sequence?: number;
  group_container_count?: number | null;
}

// Shipment Group types for batch creation
export interface ContainerInput {
  containerNumber: string;
  equipmentType?: string;
  steamshipLine?: string;
  cutoff?: string;
  erd?: string;
}

export interface BatchCreateOrderRequest {
  importExport?: 'import' | 'export';
  type?: string;
  bolNumber?: string;
  bookingNumber?: string;
  orderingPartyType?: string;
  orderingPartyId?: number;
  billToType?: string;
  billToId?: number;
  deliverToType?: string;
  deliverToId?: number;
  pickupLocationType?: string;
  pickupLocationId?: number;
  loadingLocationType?: string;
  loadingLocationId?: number;
  returnLocationType?: string;
  returnLocationId?: number;
  notes?: string;
  containers: ContainerInput[];
  [key: string]: unknown;
}

export interface BatchCreateOrderResponse {
  group: {
    id: number;
    groupNumber: string;
    bolNumber: string | null;
    bookingNumber: string | null;
    importExport: string;
    orderType: string;
    containerCount: number;
  };
  orders: Order[];
}

// For grouped display in OrderTable
export interface OrderGroup {
  isGroup: true;
  groupId: number;
  groupNumber: string;
  orders: Order[];
}

/**
 * Order Stats - per STATUS_DESIGN.md v2.1
 * Order Status (5 values only):
 *   - pending: 新建订单，尚无 Task 或所有 Task 都在等待状态
 *   - in-progress: 有任务在执行（Task 进入 unassigned 或之后的状态）
 *   - paused: 人工暂停 或 任何 Task 处于 paused 状态
 *   - completed: 所有任务完成（或部分完成+部分取消）
 *   - cancelled: 订单取消 或 所有 Task 都被取消
 */
export interface OrderStats {
  total: number;
  pending: number;      // 新建订单，尚无 Task 或所有 Task 都在等待状态
  in_progress: number;  // 有任务在执行（Task 进入 unassigned 或之后的状态）
  paused: number;       // 人工暂停 或 任何 Task 处于 paused 状态
  completed: number;    // 所有任务完成（或部分完成+部分取消）
  cancelled: number;    // 订单被取消
}

// ==================== Auth API ====================

export const authAPI = {
  register: async (data: { email: string }) => {
    // platform='web' tells backend to send a link-in-email (verification URL)
    // instead of the 6-digit code flow used by the mobile app.
    const response = await api.post('/auth/register', { ...data, platform: 'web' });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post('/auth/resend-verification', { email, platform: 'web' });
    return response.data;
  },

  completeRegistration: async (data: {
    email: string;
    username: string;
    password: string;
    role: string;
    verificationToken: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    companyName?: string;
    inviteCode?: string;
    registrationNumber?: string;
    mcDot?: string;
    scacCode?: string;
    companyAddress?: string;
    companyCity?: string;
    companyState?: string;
    companyZipCode?: string;
    serviceArea?: string;
  }) => {
    const response = await api.post('/auth/complete-registration', data);
    // Store both tokens
    if (response.data.accessToken || response.data.token) {
      localStorage.setItem(TOKEN_KEY, response.data.accessToken || response.data.token);
    }
    if (response.data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  login: async (data: { email: string; password: string }) => {
    const response = await api.post('/auth/login', data);
    // MFA-enabled users get { requires_mfa: true, mfa_challenge_token } and
    // NO access/refresh tokens — caller must follow up with mfaVerify().
    if (response.data.requires_mfa) {
      return response.data;
    }
    // Store both tokens
    if (response.data.accessToken || response.data.token) {
      localStorage.setItem(TOKEN_KEY, response.data.accessToken || response.data.token);
    }
    if (response.data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // ============ MFA (TOTP) ============

  mfaSetup: async () => {
    const response = await api.post('/auth/mfa/setup');
    return response.data; // { otpauth_url, qr_data_url, manual_entry_key }
  },

  mfaEnable: async (token: string) => {
    const response = await api.post('/auth/mfa/enable', { token, platform: 'web' });
    // Store fresh post-MFA tokens
    if (response.data.accessToken) localStorage.setItem(TOKEN_KEY, response.data.accessToken);
    if (response.data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    return response.data; // { recovery_codes, accessToken, refreshToken }
  },

  mfaVerify: async (data: { mfa_challenge_token: string; token?: string; recovery_code?: string }) => {
    const response = await api.post('/auth/mfa/verify', { ...data, platform: 'web' });
    if (response.data.accessToken || response.data.token) {
      localStorage.setItem(TOKEN_KEY, response.data.accessToken || response.data.token);
    }
    if (response.data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
    if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  },

  mfaDisable: async (data: { password: string; token?: string; recovery_code?: string }) => {
    const response = await api.post('/auth/mfa/disable', data);
    return response.data;
  },

  mfaStatus: async () => {
    const response = await api.get('/auth/mfa/status');
    return response.data; // { mfa_enabled, has_secret, recovery_codes_remaining }
  },

  logout: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      // Revoke refresh token on server
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Ignore errors, still clear local storage
      console.warn('Logout request failed, clearing local tokens anyway');
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },

  logoutAll: async () => {
    try {
      await api.post('/auth/logout-all');
    } catch {
      console.warn('Logout all request failed');
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  },

  getSessions: async () => {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const response = await api.post('/auth/refresh', { refreshToken });
    if (response.data.accessToken) {
      localStorage.setItem(TOKEN_KEY, response.data.accessToken);
    }
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Completes Google OAuth registration for a 'pending' placeholder user:
  // backend dispatches to CompanyService.attachCompanyToBoss /
  // attachEmployeeToCompany depending on role, validates & consumes the
  // invite code, flips role, and returns a fresh JWT.
  //
  // Also used for benign profile edits post-onboarding — in that case only
  // firstName/lastName/phone are honored server-side.
  updateProfile: async (data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    role?: string;                // 'boss' | 'dispatcher' | 'accountant' | 'docs_clerk' | 'manager'
    inviteCode?: string;
    // boss-only company fields
    companyName?: string;
    registrationNumber?: string;
    mcDot?: string;
    scacCode?: string;
    serviceArea?: string;
    companyAddress?: string;
    companyCity?: string;
    companyState?: string;
    companyZipCode?: string;
    companyEmail?: string;
    companyPhone?: string;
    primaryContactName?: string;
  }) => {
    const response = await api.put('/profile/update-profile', data);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  verifyResetCode: async (email: string, code: string) => {
    const response = await api.post('/auth/verify-reset-code', { email, code });
    return response.data;
  },

  resetPassword: async (email: string, code: string, password: string) => {
    const response = await api.post('/auth/reset-password', { email, code, password });
    return response.data;
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await api.put('/auth/change-password', data);
    return response.data;
  },

  // `intent` tells the backend whether this is a sign-up attempt (new user
  // creation is allowed) or a sign-in attempt (strict: existing users only —
  // backend redirects to /login?error=not_registered otherwise).
  //   - 'business' → boss/tenant registration
  //   - 'employee' → dispatcher/accountant/docs_clerk/manager registration
  //   - 'login'    → login only (default, safest)
  googleAuth: (opts?: { intent?: 'login' | 'business' | 'employee' }) => {
    const intent = opts?.intent || 'login';
    const base = `${API_BASE_URL}/auth/google`;
    window.location.href = `${base}?intent=${encodeURIComponent(intent)}`;
  },

  // Handle OAuth callback tokens (called from pages that receive tokens via URL)
  handleOAuthTokens: (accessToken: string, refreshToken?: string) => {
    if (accessToken) {
      localStorage.setItem(TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
};

// ==================== Dispatch Task API (Legacy - use taskAPI instead) ====================

/**
 * @deprecated Use taskAPI instead. This is kept for backward compatibility.
 */
export const dispatchTaskAPI = {
  getAll: async (params?: {
    status?: string;
    task_type?: string;
    driver_id?: number;
    container_id?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<DispatchTask>> => {
    const response = await api.get('/dispatch-tasks', { params });
    return response.data;
  },

  getById: async (id: number): Promise<DispatchTask> => {
    const response = await api.get(`/dispatch-tasks/${id}`);
    return response.data;
  },

  create: async (data: Partial<DispatchTask>) => {
    const response = await api.post('/dispatch-tasks', data);
    return response.data;
  },

  update: async (id: number, data: Partial<DispatchTask>) => {
    const response = await api.put(`/dispatch-tasks/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/dispatch-tasks/${id}`);
    return response.data;
  },

  assignDriver: async (id: number, driver_id: number, truck_id?: number) => {
    const response = await api.patch(`/dispatch-tasks/${id}/assign-driver`, { driver_id, truck_id });
    return response.data;
  },

  startTask: async (id: number) => {
    const response = await api.patch(`/dispatch-tasks/${id}/start`);
    return response.data;
  },

  completeTask: async (id: number, driver_notes?: string) => {
    const response = await api.patch(`/dispatch-tasks/${id}/complete`, { driver_notes });
    return response.data;
  },

  cancelTask: async (id: number, reason?: string) => {
    const response = await api.patch(`/dispatch-tasks/${id}/cancel`, { reason });
    return response.data;
  },

  getDriverTasks: async (driver_id: number, status?: string): Promise<DispatchTask[]> => {
    const response = await api.get(`/dispatch-tasks/driver/${driver_id}`, { params: { status } });
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get('/dispatch-tasks/stats');
    return response.data;
  },
};

// ==================== Trucking Company API ====================

export const companyAPI = {
  getAll: async (params?: {
    type?: string;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Company>> => {
    const response = await api.get('/trucking-companies', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Company> => {
    const response = await api.get(`/trucking-companies/${id}`);
    return response.data;
  },

  create: async (data: Partial<Company>) => {
    const response = await api.post('/trucking-companies', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Company>) => {
    const response = await api.put(`/trucking-companies/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/trucking-companies/${id}`);
    return response.data;
  },

  getByType: async (type: 'shipper' | 'warehouse' | 'carrier' | 'customer'): Promise<Company[]> => {
    const response = await api.get(`/trucking-companies/type/${type}`);
    return response.data;
  },

  getContainers: async (id: number) => {
    const response = await api.get(`/trucking-companies/${id}/containers`);
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get('/trucking-companies/stats');
    return response.data;
  },
};

// Shipper API
export const shipperAPI = {
  getAll: async (params?: {
    status?: string;
    search?: string;
    isVip?: boolean;
    creditRating?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Record<string, unknown>>> => {
    const response = await api.get('/shippers', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Record<string, unknown>> => {
    const response = await api.get(`/shippers/${id}`);
    return response.data;
  },

  create: async (data: Record<string, unknown>) => {
    const response = await api.post('/shippers', data);
    return response.data;
  },

  update: async (id: number, data: Record<string, unknown>) => {
    const response = await api.put(`/shippers/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/shippers/${id}`);
    return response.data;
  },

  getContacts: async (id: number) => {
    const response = await api.get(`/shippers/${id}/contacts`);
    return response.data;
  },

  getDocuments: async (id: number) => {
    const response = await api.get(`/shippers/${id}/documents`);
    return response.data;
  },

  getStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get('/shippers/stats');
    return response.data;
  },
};

// ==================== Warehouse Client API ====================

export const warehouseClientAPI = {
  getAll: async (params?: {
    search?: string;
    isPreferred?: boolean;
    warehouseStatus?: string;
    warehouseType?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data: Record<string, unknown>[]; total: number; limit: number; offset: number; hasMore: boolean }> => {
    const response = await api.get('/warehouse-clients', { params });
    return response.data;
  },

  getById: async (id: number): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.get(`/warehouse-clients/${id}`);
    return response.data;
  },

  getNextCode: async (): Promise<{ success: boolean; data: { warehouseCode: string } }> => {
    const response = await api.get('/warehouse-clients/next-code');
    return response.data;
  },

  create: async (warehouseData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post('/warehouse-clients', warehouseData);
    return response.data;
  },

  update: async (id: number, warehouseData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.put(`/warehouse-clients/${id}`, warehouseData);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.delete(`/warehouse-clients/${id}`);
    return response.data;
  },

  // Contacts
  getContacts: async (id: number): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    const response = await api.get(`/warehouse-clients/${id}/contacts`);
    return response.data;
  },

  createContact: async (id: number, contactData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post(`/warehouse-clients/${id}/contacts`, contactData);
    return response.data;
  },

  updateContact: async (contactId: number, contactData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.put(`/warehouse-clients/contacts/${contactId}`, contactData);
    return response.data;
  },

  deleteContact: async (contactId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/warehouse-clients/contacts/${contactId}`);
    return response.data;
  },

  // Documents
  getDocuments: async (id: number): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    const response = await api.get(`/warehouse-clients/${id}/documents`);
    return response.data;
  },

  createDocument: async (id: number, docData: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const response = await api.post(`/warehouse-clients/${id}/documents`, docData);
    return response.data;
  },

  deleteDocument: async (docId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/warehouse-clients/documents/${docId}`);
    return response.data;
  },

  // Notes
  getNotes: async (id: number): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    const response = await api.get(`/warehouse-clients/${id}/notes`);
    return response.data;
  },

  createNote: async (id: number, noteData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post(`/warehouse-clients/${id}/notes`, noteData);
    return response.data;
  },

  updateNote: async (noteId: number, noteData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.put(`/warehouse-clients/notes/${noteId}`, noteData);
    return response.data;
  },

  deleteNote: async (noteId: number): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.delete(`/warehouse-clients/notes/${noteId}`);
    return response.data;
  },

  // Payments
  getPayments: async (id: number): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    const response = await api.get(`/warehouse-clients/${id}/payments`);
    return response.data;
  },

  upsertPayment: async (id: number, paymentData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post(`/warehouse-clients/${id}/payments`, paymentData);
    return response.data;
  },

  // Reminders
  getReminders: async (id: number): Promise<{ success: boolean; data: Record<string, unknown>[] }> => {
    const response = await api.get(`/warehouse-clients/${id}/reminders`);
    return response.data;
  },

  createReminder: async (id: number, reminderData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post(`/warehouse-clients/${id}/reminders`, reminderData);
    return response.data;
  },

  updateReminder: async (reminderId: number, updates: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.put(`/warehouse-clients/reminders/${reminderId}`, updates);
    return response.data;
  },

  deleteReminder: async (reminderId: number): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.delete(`/warehouse-clients/reminders/${reminderId}`);
    return response.data;
  },

  // Statistics
  getStats: async (): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.get('/warehouse-clients/stats');
    return response.data;
  },

  // Summary
  getSummary: async (id: number): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.get(`/warehouse-clients/${id}/summary`);
    return response.data;
  },
};

// ==================== Port API ====================

export const portAPI = {
  getAll: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data: Record<string, unknown>[]; total: number }> => {
    const response = await api.get('/ports', { params });
    return response.data;
  },

  getById: async (id: number): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.get(`/ports/${id}`);
    return response.data;
  },

  getSummary: async (id: number): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.get(`/ports/${id}/summary`);
    return response.data;
  },

  create: async (portData: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.post('/ports', portData);
    return response.data;
  },

  update: async (id: number, updates: Record<string, unknown>): Promise<{ success: boolean; message: string; data: Record<string, unknown> }> => {
    const response = await api.put(`/ports/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/ports/${id}`);
    return response.data;
  },
};

// ==================== Order API ====================

export const orderAPI = {
  getAll: async (params?: {
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    driverAssignment?: 'all' | 'assigned' | 'unassigned';
    // Three-party role filters
    orderingPartyType?: 'client' | 'shipper' | 'warehouse' | 'port';
    orderingPartyId?: number;
    billToType?: 'client' | 'shipper' | 'warehouse' | 'port';
    billToId?: number;
    deliverToType?: 'client' | 'shipper' | 'warehouse' | 'port';
    deliverToId?: number;
    // Legacy filters (still supported)
    clientId?: number;
    shipperId?: number;
    warehouseId?: number;
    portId?: number;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Order>> => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  getStats: async (params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<OrderStats> => {
    const response = await api.get('/orders/stats', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Order> => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },

  checkDuplicate: async (params: { containerNumber?: string; bolNumber?: string }): Promise<{ duplicates: Array<{ id: number; order_id: string; container_number: string; bol_number: string; status: string; import_export: string }> }> => {
    const response = await api.get('/orders/check-duplicate', { params });
    return response.data;
  },

  /**
   * 这个目的地上一次建单选的是 Drop&Pick 还是 Liveload。
   * 没有历史时 type 为 null，调用方保持自己的默认值。
   */
  getLastTypeForDestination: async (params: {
    locationType: 'warehouse' | 'client';
    locationId: number;
  }): Promise<{ type: 'Drop&Pick' | 'Liveload' | null; fromOrderId: string | null; lastUsedAt: string | null }> => {
    const response = await api.get('/orders/last-type', { params });
    return response.data;
  },

  create: async (data: {
    containerNumber: string;
    // Import/Export type - determines business workflow
    importExport?: 'import' | 'export';
    // Three-party role fields (preferred)
    orderingPartyType?: 'client' | 'shipper' | 'warehouse' | 'port';
    orderingPartyId?: number | null;
    billToType?: 'client' | 'shipper' | 'warehouse' | 'port';
    billToId?: number | null;
    deliverToType?: 'client' | 'shipper' | 'warehouse' | 'port';
    deliverToId?: number | null;
    // Legacy fields (still supported for backward compatibility)
    shipperId?: number | null;
    clientId?: number | null;
    type?: string;
    destinationWarehouseId?: number | null;
    chassisId?: number | null;
    rateType?: string;
    totalAmount?: number;
    bolNumber?: string | null;
    notes?: string | null;
  }) => {
    const response = await api.post('/orders', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Order>) => {
    const response = await api.put(`/orders/${id}`, data);
    return response.data;
  },

  /**
   * Scan 补全：把邮件里读到、订单上还空着的那几格写进去。
   * body 直接用数据库列名（snake_case）—— 后端 update 的白名单就是按列名比的，
   * 转成 camelCase 再转回来只会多一层可以对错的映射。
   */
  scanFill: async (orderCode: string, fields: Record<string, unknown>) => {
    const response = await api.put(`/orders/${orderCode}`, fields);
    return response.data;
  },

  /**
   * Scan 逐柜补全：一封到港预报里 22 个柜就是 22 张单，收成一个请求发出去。
   * 每张单在后端各自一个事务，回来的 results 逐张说明成没成。
   */
  scanFillBatch: async (updates: Array<{ orderCode: string; fields: Record<string, unknown> }>) => {
    const response = await api.post('/orders/scan-fill', { updates });
    return response.data;
  },

  /**
   * Scan 逐柜建单：一封信里十几个 DO，一个附件一个柜，各带各的提单号和送货地址。
   * 建的是十几张互不相干的独立单（不是「一单多柜」），每张在后端各自一个事务，
   * 回来的 results 逐个柜说明成没成。
   */
  scanCreateBatch: async (orders: Array<Record<string, unknown>>) => {
    const response = await api.post('/orders/scan-create', { orders });
    return response.data;
  },

  delete: async (id: string, force?: boolean) => {
    const response = await api.delete(`/orders/${id}`, {
      params: force ? { force: 'true' } : undefined
    });
    return response.data;
  },

  /** Batch create orders (On Bill / 一单多柜) */
  batchCreate: async (data: BatchCreateOrderRequest): Promise<BatchCreateOrderResponse> => {
    const response = await api.post('/orders/batch', data);
    return response.data;
  },

  /** Delete entire shipment group */
  deleteGroup: async (groupId: number, force?: boolean): Promise<void> => {
    await api.delete(`/orders/group/${groupId}`, {
      params: force ? { force: 'true' } : undefined
    });
  },
};

// ==================== Client API ====================

export interface BackendClient {
  id: number;
  company_name: string;
  client_code: string;
  source_type: 'shipper' | 'warehouse' | 'third-party';
  source_id: number | null;
  short_name: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  payment_terms: string | null;
  current_balance: string;
  payment_status: string;
  status: string;
  total_orders: number | null;
  total_revenue: string | null;
  total_outstanding: string | null;
  is_vip: boolean;
  created_at: string;
  updated_at: string;
}

interface ClientListResponse {
  data: BackendClient[];
  total: number;
  limit: number;
  offset: number;
}

export const clientAPI = {
  getAll: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    source_type?: string;
    search?: string;
  }): Promise<ClientListResponse> => {
    const response = await api.get('/clients', { params });
    return response.data;
  },

  getById: async (id: number): Promise<BackendClient> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },
};

// ==================== Invoice API ====================

export interface Invoice {
  id: number;
  invoiceId: string;           // System display ID (e.g., "INV25O001")
  invoiceNumber?: string | null; // Finance official number (e.g., "INV-2025-001")
  orderId: string;
  clientId: number;
  clientName: string;
  clientType: string;
  rateType: 'all-in' | 'drayage-only';
  pricingType: 'flat' | 'dynamic';
  baseAmount: string | number;
  extraAmount: string | number;
  taxRate?: string | number;
  taxAmount: string | number;
  discountAmount: string | number;
  totalAmount: string | number;
  paymentTerm: 'net15' | 'net30' | 'net60' | 'weekly' | 'monthly' | 'custom';
  status: 'draft' | 'pending' | 'generated' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  issuedDate: string;
  sentDate?: string | null;
  dueDate?: string | null;
  paidDate?: string | null;
  statementId?: number | null;
  createdAt: string;
  notes?: string | null;
  extraCharges?: Record<string, unknown>[];
  // Order context fields (from enhanced v_invoice_details view)
  containerNumber?: string | null;
  orderType?: string | null;
  orderStatus?: string | null;
  importExport?: string | null;
  completedAt?: string | null;
  orderCreatedAt?: string | null;
  taskCount?: number;
  taskIds?: number[] | null;
  orderingPartyName?: string | null;
  billToName?: string | null;
  shipperName?: string | null;
  portName?: string | null;
  warehouseName?: string | null;
  bolNumber?: string | null;
}

export interface InvoiceTask {
  id: number;
  taskType: string;
  taskSubtype: string | null;
  status: string;
  originType: string | null;
  originId: number | null;
  originName: string | null;
  destinationType: string | null;
  destinationId: number | null;
  destinationName: string | null;
  driverName: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface InvoiceStats {
  draft: number;
  pending: number;
  awaitingPay: number;
  paid: number;
  overdue: number;
  cancelled: number;
  total: number;
}

interface InvoiceSummary {
  totalInvoices: string | number;
  totalAmount: string | number;
  paidAmount: string | number;
  pendingAmount: string | number;
  overdueAmount: string | number;
}

interface InvoiceResponse {
  success: boolean;
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
  summary: InvoiceSummary;
  stats: InvoiceStats;
}

// Helper function to transform snake_case API response to camelCase
function transformInvoiceFromAPI(data: Record<string, unknown>): Invoice {
  return {
    id: data.id,
    invoiceId: data.invoice_id,
    invoiceNumber: data.invoice_number,
    orderId: data.order_id,
    clientId: data.client_id,
    clientName: data.client_name,
    clientType: data.client_type,
    rateType: data.rate_type,
    pricingType: data.pricing_type,
    baseAmount: data.base_amount,
    extraAmount: data.extra_amount,
    taxRate: data.tax_rate,
    taxAmount: data.tax_amount,
    discountAmount: data.discount_amount,
    totalAmount: data.total_amount,
    paymentTerm: data.payment_term,
    status: data.status,
    issuedDate: data.issued_date,
    sentDate: data.sent_date,
    dueDate: data.due_date,
    paidDate: data.paid_date,
    statementId: data.statement_id,
    createdAt: data.created_at,
    notes: data.notes,
    extraCharges: data.extra_charges || [],
    // Order context fields
    containerNumber: data.container_number,
    orderType: data.order_type,
    orderStatus: data.order_status,
    importExport: data.import_export,
    completedAt: data.completed_at,
    orderCreatedAt: data.order_created_at,
    taskCount: data.task_count != null ? Number(data.task_count) : 0,
    taskIds: data.task_ids,
    orderingPartyName: data.ordering_party_name,
    billToName: data.bill_to_name,
    shipperName: data.shipper_name,
    portName: data.port_name,
    warehouseName: data.warehouse_name,
    bolNumber: data.bol_number,
  } as Invoice;
}

function transformInvoiceTaskFromAPI(data: Record<string, unknown>): InvoiceTask {
  return {
    id: data.id as number,
    taskType: data.task_type as string,
    taskSubtype: data.task_subtype as string | null,
    status: data.status as string,
    originType: data.origin_type as string | null,
    originId: data.origin_id as number | null,
    originName: data.origin_name as string | null,
    destinationType: data.destination_type as string | null,
    destinationId: data.destination_id as number | null,
    destinationName: data.destination_name as string | null,
    driverName: data.driver_name as string | null,
    completedAt: data.completed_at as string | null,
    createdAt: data.created_at as string,
  };
}

export const invoiceAPI = {
  getAll: async (params?: {
    orderId?: string;
    taskId?: number;
    clientId?: number;
    status?: string;
    orderStatus?: string;
    search?: string;
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<InvoiceResponse> => {
    // Filter out undefined/null/empty params
    const cleanParams: Record<string, string | number> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          cleanParams[key] = value;
        }
      });
    }
    const response = await api.get('/invoices', { params: cleanParams });
    // The response interceptor strips `success` and extracts inner structure
    // For paginated responses (has total + limit), it preserves full shape
    const data = response.data as {
      success?: boolean;
      data: Record<string, unknown>[];
      total: number;
      page: number;
      limit: number;
      summary: InvoiceSummary;
      stats: Record<string, string | number>;
    };

    const rawStats = data.stats || {};
    const stats: InvoiceStats = {
      draft: Number(rawStats.draft || 0),
      pending: Number(rawStats.pending || 0),
      awaitingPay: Number(rawStats.awaiting_pay || 0),
      paid: Number(rawStats.paid || 0),
      overdue: Number(rawStats.overdue || 0),
      cancelled: Number(rawStats.cancelled || 0),
      total: Number(rawStats.total || 0),
    };

    return {
      success: true,
      data: (data.data || []).map(transformInvoiceFromAPI),
      total: data.total,
      page: data.page,
      limit: data.limit,
      summary: data.summary,
      stats,
    };
  },

  getById: async (id: number): Promise<Invoice> => {
    const response = await api.get(`/invoices/${id}`);
    return transformInvoiceFromAPI(response.data as Record<string, unknown>);
  },

  getInvoiceTasks: async (id: number): Promise<InvoiceTask[]> => {
    const response = await api.get(`/invoices/${id}/tasks`);
    const rows = (response.data as Record<string, unknown>[]) || [];
    return rows.map(transformInvoiceTaskFromAPI);
  },

  updateStatus: async (id: number, status: string) => {
    const response = await api.patch(`/invoices/${id}/status`, { status });
    return transformInvoiceFromAPI(response.data as Record<string, unknown>);
  },

  assignNumber: async (id: number, invoiceNumber: string) => {
    const response = await api.post(`/invoices/${id}/assign-number`, {
      invoice_number: invoiceNumber
    });
    return transformInvoiceFromAPI(response.data as Record<string, unknown>);
  },

  cancel: async (id: number) => {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },

  exportPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },
};

// ==================== Driver Payment API ====================

interface DriverPayment {
  id: number;
  taskId: number;
  driverId: number;
  driverName?: string;
  containerNumber?: string;
  taskType?: string;
  from?: string;
  to?: string;
  date: string;
  baseFare: number;
  waitingFee: number;
  extraCharges: number;
  totalAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'disputed' | 'cancelled';
  isPaid: boolean;
  paidAt?: string;
  approvedAt?: string;
  approvedBy?: number;
  createdAt: string;
  updatedAt?: string;
}

interface DriverPaymentSummary {
  driverId: number;
  driverName: string;
  totalEarnings: number;
  pendingAmount: number;
  paidAmount: number;
  taskCount: number;
  avgPerTask: number;
  periodStart?: string;
  periodEnd?: string;
}

interface PaymentListResponse {
  success: boolean;
  data: DriverPayment[];
  total: number;
  page: number;
  limit: number;
  summary?: {
    total_payments: number;
    total_amount: number;
    total_base: number;
    total_extras: number;
    paid_amount: number;
    pending_amount: number;
  };
}

export const driverPaymentAPI = {
  // Get list of payments with filters
  getAll: async (params?: {
    taskId?: number;
    driverId?: number;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    periodStart?: string;
    periodEnd?: string;
    page?: number;
    limit?: number;
  }): Promise<PaymentListResponse> => {
    const response = await api.get('/driver-payments', { params });
    return response.data;
  },

  // Get payment by ID
  getById: async (id: number): Promise<DriverPayment> => {
    const response = await api.get(`/driver-payments/${id}`);
    return response.data;
  },

  // Generate payment for a task
  generate: async (taskId: number): Promise<DriverPayment> => {
    const response = await api.post('/driver-payments/generate', { taskId });
    return response.data;
  },

  // Batch generate payments
  batchGenerate: async (taskIds: number[]): Promise<{ success: boolean; payments: DriverPayment[]; errors: { taskId: number; message: string }[] }> => {
    const response = await api.post('/driver-payments/batch-generate', { taskIds });
    return response.data;
  },

  // Approve payment
  approve: async (id: number): Promise<DriverPayment> => {
    const response = await api.post(`/driver-payments/${id}/approve`);
    return response.data;
  },

  // Mark payment as paid
  markAsPaid: async (id: number): Promise<DriverPayment> => {
    const response = await api.post(`/driver-payments/${id}/mark-paid`);
    return response.data;
  },

  // Batch approve payments
  batchApprove: async (paymentIds: number[]): Promise<{ success: boolean; approved: number[]; errors: { paymentId: number; message: string }[] }> => {
    const response = await api.post('/driver-payments/batch-approve', { paymentIds });
    return response.data;
  },

  // Batch mark as paid
  batchMarkAsPaid: async (paymentIds: number[]): Promise<{ success: boolean; paid: number[]; errors: { paymentId: number; message: string }[] }> => {
    const response = await api.post('/driver-payments/batch-mark-paid', { paymentIds });
    return response.data;
  },

  // Get period stats for all drivers
  getPeriodStats: async (params?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Array<{ driver_id: number; task_count: number; total_amount: number }>> => {
    const response = await api.get('/driver-payments/period-stats', { params });
    return response.data;
  },

  // Get driver summary
  getDriverSummary: async (driverId: number, params?: {
    periodStart?: string;
    periodEnd?: string;
  }): Promise<DriverPaymentSummary> => {
    const response = await api.get(`/driver-payments/driver/${driverId}/summary`, { params });
    return response.data;
  },
};

// ==================== Processing Flow Types ====================

export interface ProcessingStep {
  id: number;
  flow_instance_id: number;
  owner_type: 'order' | 'task';
  owner_id: number;
  template_step_id?: number;
  name: string;
  description?: string;
  order_index: number;
  status: 'pending' | 'in_progress' | 'blocked' | 'skipped' | 'completed' | 'failed';
  is_required: boolean;
  can_skip: boolean;
  required_role: 'dispatcher' | 'driver' | 'accounting' | 'system' | 'any';
  assignee_user_id?: number;
  started_at?: string;
  completed_at?: string;
  due_at?: string;
  blocked_reason?: string;
  payload?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface FlowInstance {
  id: number;
  subject_type: 'order' | 'task';
  owner_id: number;
  type_code: string;
  template_id: number;
  template_version: number;
  status: 'pending' | 'in_progress' | 'blocked' | 'skipped' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

interface ProcessingEvent {
  id: number;
  step_id: number;
  event: 'status_changed' | 'field_updated' | 'attachment_added' | 'comment_added' | 'auto_transition' | 'sla_breached' | 'assigned' | 'reassigned';
  from_status?: string;
  to_status?: string;
  data?: Record<string, unknown>;
  created_by?: number;
  created_at: string;
}

// ==================== Processing Flow API ====================

export const processingAPI = {
  // Get flow instance for an order or task
  getFlowInstance: async (subjectType: 'order' | 'task', ownerId: number): Promise<FlowInstance> => {
    const response = await api.get(`/processing/${subjectType}/${ownerId}`);
    return response.data;
  },

  // Get all processing steps for an order or task
  getSteps: async (subjectType: 'order' | 'task', ownerId: number): Promise<{ steps: ProcessingStep[], total: number }> => {
    const response = await api.get(`/processing/${subjectType}/${ownerId}/steps`);
    return response.data;
  },

  // Get timeline/events for an order or task
  getTimeline: async (subjectType: 'order' | 'task', ownerId: number): Promise<{ events: ProcessingEvent[], total: number }> => {
    const response = await api.get(`/processing/${subjectType}/${ownerId}/timeline`);
    return response.data;
  },

  // Update step status
  updateStepStatus: async (stepId: number, status: string, payload?: Record<string, unknown>) => {
    const response = await api.patch(`/processing/steps/${stepId}/status`, { status, payload });
    return response.data;
  },

  // Start a step
  startStep: async (stepId: number) => {
    const response = await api.post(`/processing/steps/${stepId}/start`);
    return response.data;
  },

  // Complete a step
  completeStep: async (stepId: number, payload?: Record<string, unknown>) => {
    const response = await api.post(`/processing/steps/${stepId}/complete`, { payload });
    return response.data;
  },

  // Skip a step
  skipStep: async (stepId: number, reason?: string) => {
    const response = await api.post(`/processing/steps/${stepId}/skip`, { reason });
    return response.data;
  },

  // Fail a step
  failStep: async (stepId: number, reason: string) => {
    const response = await api.post(`/processing/steps/${stepId}/fail`, { reason });
    return response.data;
  },

  // Assign a step to a user
  assignStep: async (stepId: number, userId: number) => {
    const response = await api.post(`/processing/steps/${stepId}/assign`, { userId });
    return response.data;
  },

  // Get events for a specific step
  getStepEvents: async (stepId: number): Promise<{ events: ProcessingEvent[], total: number }> => {
    const response = await api.get(`/processing/steps/${stepId}/events`);
    return response.data;
  },

  // Get processing stats
  getStats: async (): Promise<Record<string, unknown>> => {
    const response = await api.get('/processing/stats');
    return response.data;
  },
};

// ==================== Executive Dashboard API ====================

export interface ExecutiveDashboardData {
  overview: {
    totalTasks: number;
    completedTasks: number;
    inProgressTasks: number;
    totalRevenue: number;
    revenueChange: number;
  };
  taskTypes: Record<string, number>;
  awaitingList: {
    onVessel: number;
    onHold: number;
    unassigned: number;
    assigned: number;
    onYard: number;
    inTransit: number;
  };
  revenueChart: Array<{
    period: string;
    revenue: number;
    taskCount: number;
  }>;
  topWarehouses: Array<{
    id: number;
    name: string;
    address: string;
    deliveries: number;
  }>;
}

export interface ExecutiveOrderDashboardData {
  overview: {
    totalOrders: number;
    completedOrders: number;
    inProgressOrders: number;
    totalRevenue: number;
    revenueChange: number;
  };
  orderTypes: Record<string, number>;
  orderStatusSnapshot: {
    pending: number;
    inProgress: number;
    paused: number;
    cancelled: number;
  };
  revenueChart: Array<{ period: string; revenue: number; orderCount: number }>;
  topClients: Array<{ id: number; name: string; address: string; orders: number }>;
}

export const executiveAPI = {
  getDashboard: async (params: {
    date_from: string;
    date_to: string;
    chart_period?: string;
  }): Promise<ExecutiveDashboardData> => {
    const response = await api.get('/executive/dashboard', { params });
    return response.data;
  },
  getOrderDashboard: async (params: {
    date_from: string;
    date_to: string;
    chart_period?: string;
  }): Promise<ExecutiveOrderDashboardData> => {
    const response = await api.get('/executive/dashboard/orders', { params });
    return response.data;
  },
};

export default api;
